import _ from 'lodash'
import { DateTime } from 'luxon'
import { itemStatuses, dateTimeFormat, authErrors, env, rentalStatuses } from './constants'
import locations from './locations'

export const makeItemGroup = (i) => {
  let group = {}
  i.forEach(item => {
    if (typeof item?.name !== 'undefined' &&
    typeof item?.returnedStatus !== 'undefined') {
      let suffix = item?.returnedStatus === itemStatuses.returned ? item?.returnedTimeDifference : ''
      let key = `${item?.name}-${item?.returnedStatus}-${suffix}`
      if (Object.keys(group).includes(key)) {
        group[key] = {
         ...group[key],
          quantity: group[key].quantity + 1
        }
      } else {
        let {
          id,
          returnedStatus,
          catalogObjectId,
          imageUrl,
          name,
          returnDate = null,
          returnedTimeDifference = ""
        } = item
        group[key] = {
          ...item,
          id,
          returnedStatus,
          returnedTimeDifference,
          catalogObjectId,
          imageUrl,
          name,
          returnDate,
          quantity: 1
        }
      }
    }
  })
  return Object.values(group)
}
export const makeSets = ({ data = [] }) => {
  let items = data.map(item => ({
    ...item,
    dueDate: formatDate(item?.dueDate),
    dueDateOriginal: item?.dueDate,
    dueDateTime: formatDate(item?.dueDate, dateTimeFormat),
  }))
  return Object.entries(_.groupBy(items, 'dueDate'))
    .map(([key, value]) => ({
      id: key,
      dueDate: key,
      items: value,
      displayItems: makeItemGroup(value),
      dueDateTime: value[0].dueDateTime
    }))
    .sort((a,b) => new Date(a.dueDate) - new Date(b.dueDate))
}
export const makeIPUSets = ({ data = [] }) => {
  let items = data.map(item => ({ ...item, dueDate:  formatDate(item?.dueDate)}))
  
  return Object.entries(_.groupBy(items, 'dueDate'))
    .map(([key, value]) => ({
      id: key,
      dueDate: key,
      items: value
    }))
    .filter(set => set.items.length > 0)
    .sort((a,b) => new Date(a.dueDate) - new Date(b.dueDate))
}

export const isLater = (date) => {
  if (!date) return false
  let now = new Date()
  if (Date.parse(date)) {
    let parsedDate = new Date(date)
    return parsedDate > now
  } else {
    return false
  }
}
const getHoursText = (days, hours) => {
  if (hours === 0) return '';
  let uppersand = days === 0 ? '' : ' & ';
  let text = hours > 1 ? 'hrs' : 'hr';
  return uppersand + `${hours}${text}`
}

const getDayText = (days) => {
  if (days === 0) return '';
  let text = days > 1 ? 'days' : 'day';
  return `${days} ${text}`
}

export let getDelta = (returnDate = '', dueDate = '') => {
  if (dueDate === '' || dueDate === null) {
    return 'Invalid Due Date'
  }
  if (returnDate === '' || returnDate === null) {
    return 'Invalid Return Date'
  }
  let dueDateUTC
  let returnDateUTC
  try {
    dueDateUTC = dueDate.toMillis()
  } catch {
    dueDateUTC = new Date(dueDate).getTime()
  }
  try {
    returnDateUTC = returnDate.toMillis()
  } catch {
    returnDateUTC = new Date(returnDate).getTime()
  }
  const dueDateTime = DateTime.fromMillis(dueDateUTC)
  const returnDateTime = DateTime.fromMillis(returnDateUTC)
  const diff = returnDateTime.diff(dueDateTime, ["days", "hours"])
  let days = Math.floor(Math.abs(diff.toObject().days))
  let hours = Math.floor(Math.abs(diff.toObject().hours))
  let msg = getDayText(days) + getHoursText(days, hours)
  let statuses = ['Early', 'Late']
  let isEarly = diff.toObject().days < 0 || diff.toObject().hours < 0
  let status = isEarly ? statuses[0] : statuses[1]
  let delta = msg === '' ? 'On Time' : msg + ' ' + status
  return delta
}
export const pruneValues = (val = {}, init = {}) => {
  let values = { ...val }
  let initial = { ...init }
  delete values.returnStore
  delete values.returnStoreId
  delete initial.returnStore
  delete initial.returnStoreId
  Object.keys(initial).forEach(key => {
    if (!key.includes('damaged')) {
      if(values[key] === initial[key] && values[`${key}-damaged`] === initial[`${key}-damaged`]) {
        delete values[key]
        delete initial[key]
        delete values[`${key}-damaged`]
        delete initial[`${key}-damaged`]
      }
    }
  })
  return values
}

export const formatPrice = (totalPrice) => {
  if (typeof totalPrice === 'undefined') return ''
  let price = `${parseFloat(totalPrice ? totalPrice : 0)/100}`
  return price.includes('.') ? price : `${price}.00`
}

export const getLostCount = (quantity = '', returned = '') => {
  if (quantity == '' || returned == '') {
    return ''
  } else {
    let diff = parseInt(quantity, 10) - parseInt(returned, 10)
    if (diff > 0) {
      return diff == parseInt(quantity, 10) ? 'All' : diff
    } else {
      return '0'
    }
  }
}

export const formatDate = (date = '', format = 'ccc LLL dd, yyyy') => {
  let hawaiiTime = getDate(date)
  if (!hawaiiTime) return 'No date provided'
  return hawaiiTime.toFormat(format)
}

export const filterStoresByIds = (allStores, userStoreIds = []) => {
  if (!Array.isArray(userStoreIds) || userStoreIds.length < 1) return [] 
  return allStores.filter(store => userStoreIds.includes(store.id))
}

export const hasNoData = (source, selector) => {
  return !Object.keys(source.data).includes(selector) ||
    (source.data[selector] &&
      source.data[selector].length === 0)
}

export const getByKey = (obj, key) => {
  try {
    let result = obj[key]
    return typeof result !== 'undefined' ? result : null;
  } catch (e) {
    console.log(e)
    return null
  }
}

export const getStoreBySquareId = (stores = [], id ='') => {
  if(!id) return null
  return stores.find(s => s.id === id) ?? null
}

export const makeOption = (store = null) => {
  if (!store) return null
  return { value: store.id, label: store.name }
}
export const makeOptions = (stores = []) => {
  if (!Array.isArray(stores) && !stores) return []
  return stores.map(makeOption)
}

export const makeStore = ({ value, label }) => ({ id: value,  name: label })

export const getUserStoreIds = (user) => {
  if (!user && !user?.storeIds && !Array.isArray(user?.storeIds)) return []
  return user?.storeIds
}

export const getStores = () => locations[env] || []

export const makeUser = ({ user, claims }) => {
  if (!user) return null
  const stores = getStores()
  const { name, email, photoURL, uid } = user
  const { isActive = false, storeIds = [], defaultStoreId = '' } = claims
  if (!isActive) throw new Error(authErrors.not_active)
  if (!Array.isArray(storeIds) || storeIds.length < 1) throw new Error(authErrors.no_stores)
  const defaultStore = getStoreBySquareId(stores, defaultStoreId)
  return {
    uid,
    name: name ?? claims.name,
    email,
    photoURL,
    isActive,
    defaultStore,
    storeIds,
    stores: filterStoresByIds(stores, storeIds)
  }
}

export const getRentalState = (pageType) => ({
  isNew: pageType === rentalStatuses.new,
  isOpen: pageType === rentalStatuses.open,
  isClosed: pageType === rentalStatuses.closed,
  isExtension: pageType === rentalStatuses.extended
})

export const getDate = (date) => {
  if (date == '' || date === null || typeof date === 'undefined') {
    return null
  } else {
    let dateValue
    try {
      dateValue = DateTime.fromMillis(date.toMillis())
    } catch {
      dateValue = DateTime.fromISO(date)
    }
    return dateValue.setZone('Pacific/Honolulu')
  }
}

export const getNow = () => {
  return DateTime.now().setZone('Pacific/Honolulu')
}
